// --- FILE: src/pages/FinanzasPage.jsx ---
// Pantalla de Finanzas: ingresos y egresos manuales, categorías configurables,
// ganancia y rentabilidad (positivo/negativo).
//
// NOTA: Los ingresos de VENTAS del ERP (DynamiaERP) todavía NO están conectados
// aquí (este proyecto consulta distinto). Por ahora la ganancia se calcula con
// los ingresos MANUALES. Ver TODO(lo-otro) más abajo para conectarlos luego.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, onSnapshot, addDoc, deleteDoc, doc, query, updateDoc,
  where, orderBy, serverTimestamp, getDocs, setDoc,
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ComposedChart, Area, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import {
  Wallet, TrendingUp, TrendingDown, DollarSign, Trash2,
  RefreshCw, ChevronLeft, ChevronRight, Scale,
  Settings, Pencil, ArrowUpCircle, ArrowDownCircle, Save, Check, X, AlertTriangle, CalendarDays,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { db } from '@/services/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { useDynamiaERP } from '@/hooks/useDynamiaERP';
import DatePresetButtons, { getPresetRange } from '@/components/common/DatePresetButtons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import DashboardHeader from '@/components/dashboard/DashboardHeader';

const COMPANY_NAME = 'GreenVape';

// Categorías por defecto — se siembran en Firestore la primera vez.
const DEFAULT_CATEGORIES = [
  { id: 'nomina',           name: 'Nómina',         color: '#6366f1', type: 'expense' },
  { id: 'renta',            name: 'Renta',          color: '#f59e0b', type: 'expense' },
  { id: 'inventario',       name: 'Inventario',     color: '#10b981', type: 'expense' },
  { id: 'marketing',        name: 'Marketing',      color: '#ec4899', type: 'expense' },
  { id: 'utilidades',       name: 'Utilidades',     color: '#8b5cf6', type: 'expense' },
  { id: 'operaciones',      name: 'Operaciones',    color: '#14b8a6', type: 'expense' },
  { id: 'otros',            name: 'Otros',          color: '#64748b', type: 'expense' },
  { id: 'ingreso_efectivo', name: 'Efectivo / ATH', color: '#0ea5e9', type: 'income'  },
  { id: 'ingreso_otro',     name: 'Otros ingresos', color: '#22c55e', type: 'income'  },
];

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

const fmtShort = (n) =>
  Math.abs(n) >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${(n || 0).toFixed(0)}`;

const collForKind = (kind) => (kind === 'income' ? 'incomes' : 'expenses');

const selectCls =
  'w-full h-10 rounded-md border border-white/10 bg-white/5 text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/30';

// ── Chart tooltip ────────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-white/50 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }} className="font-semibold">
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
}

// ── Category manager modal ───────────────────────────────────────────────────────
function CategoryManager({ open, onClose, categories, onToast }) {
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', color: '#6366f1', type: 'expense' });

  const resetForm = () => { setEditingId(null); setForm({ name: '', color: '#6366f1', type: 'expense' }); };
  const startEdit = (c) => { setEditingId(c.id); setForm({ name: c.name, color: c.color, type: c.type }); };

  const handleSaveCat = async () => {
    if (!form.name.trim()) { onToast({ title: 'Ponle un nombre a la categoría', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, 'financeCategories', editingId), { name: form.name.trim(), color: form.color, type: form.type });
        onToast({ title: 'Categoría actualizada' });
      } else {
        await addDoc(collection(db, 'financeCategories'), { name: form.name.trim(), color: form.color, type: form.type });
        onToast({ title: 'Categoría creada' });
      }
      resetForm();
    } catch (err) {
      onToast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
    setSaving(false);
  };

  const handleDeleteCat = async (id) => {
    try {
      await deleteDoc(doc(db, 'financeCategories', id));
      onToast({ title: 'Categoría eliminada' });
      setConfirmDeleteId(null);
      if (editingId === id) resetForm();
    } catch (err) {
      onToast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const groups = [
    { label: 'Ingresos', type: 'income' },
    { label: 'Egresos', type: 'expense' },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { resetForm(); onClose(); } }}>
      <DialogContent aria-describedby={undefined} className="bg-gray-900 border-white/10 text-white sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="h-4 w-4" /> Categorías</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {groups.map((g) => {
            const list = categories.filter((c) => c.type === g.type).sort((a, b) => a.name.localeCompare(b.name));
            return (
              <div key={g.type}>
                <p className="text-xs uppercase tracking-wide text-white/40 mb-1.5">{g.label}</p>
                <div className="space-y-1">
                  {list.length === 0 && <p className="text-xs text-white/25 py-1">Sin categorías de {g.label.toLowerCase()}</p>}
                  {list.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                      <span className="h-3.5 w-3.5 rounded-full shrink-0" style={{ background: c.color }} />
                      <span className="text-sm text-white/80 flex-1 truncate">{c.name}</span>
                      {confirmDeleteId === c.id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-red-300 mr-1">¿Eliminar?</span>
                          <button onClick={() => handleDeleteCat(c.id)} className="p-1 rounded text-red-400 hover:bg-red-500/10"><Check className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setConfirmDeleteId(null)} className="p-1 rounded text-white/40 hover:bg-white/10"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button onClick={() => startEdit(c)} className="p-1 rounded text-blue-400/70 hover:text-blue-400 hover:bg-blue-500/10"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setConfirmDeleteId(c.id)} className="p-1 rounded text-white/30 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-3">
            <p className="text-xs font-semibold text-white/60">{editingId ? 'Editar categoría' : 'Nueva categoría'}</p>
            <div className="flex gap-2">
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                className="h-10 w-10 rounded cursor-pointer bg-transparent border border-white/10 shrink-0"
                title="Color"
              />
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nombre (ej. Servicios, Comisiones…)"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25"
              />
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="w-32 h-10 rounded-md border border-white/10 bg-white/5 text-white px-2 text-sm shrink-0 focus:outline-none"
              >
                <option value="income" className="bg-gray-900">Ingreso</option>
                <option value="expense" className="bg-gray-900">Egreso</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              {editingId && (
                <Button onClick={resetForm} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 h-8 px-3">Cancelar</Button>
              )}
              <Button onClick={handleSaveCat} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3">
                {saving ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                {editingId ? 'Guardar' : 'Agregar'}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => { resetForm(); onClose(); }} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 h-9 px-4">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Summary card ───────────────────────────────────────────────────────────────
function SummaryCard({ title, value, icon: Icon, color, prefix = '$', suffix = '', note, noteTone = 'muted' }) {
  const noteClass = noteTone === 'good' ? 'text-emerald-400' : noteTone === 'bad' ? 'text-red-400' : 'text-white/30';
  const isNegative = value !== null && value < 0;
  return (
    <div className="rounded-xl border border-white/8 bg-white/3 p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-white/50 text-sm">{title}</p>
        <div className="p-2 rounded-lg" style={{ background: color + '20' }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
      </div>
      {value !== null ? (
        <p className="text-2xl font-bold font-mono" style={{ color: isNegative ? '#f43f5e' : '#ffffff' }}>
          {isNegative ? '−' : ''}{prefix}{Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{suffix}
        </p>
      ) : (
        <p className="text-white/30 text-lg">—</p>
      )}
      {note && <p className={`text-xs mt-1 ${noteClass}`}>{note}</p>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function FinanzasPage() {
  const { user } = useAuth();
  const { toast, toasts, dismiss } = useToast();
  const { fetchUnifiedReport } = useDynamiaERP();

  // Período: filtros rápidos (por defecto "Este mes": es un estado de resultados)
  const [preset, setPreset] = useState('mes');
  const [range, setRange] = useState(() => getPresetRange('mes'));
  const [showCustom, setShowCustom] = useState(false);
  const fromStr = format(range.from, 'yyyy-MM-dd');
  const toStr = format(range.to, 'yyyy-MM-dd');
  // Mes al que corresponde el período si es un mes completo o el mes en curso
  const month = (preset === 'mes' || preset === 'mes-anterior') ? startOfMonth(range.from) : null;

  // Ingresos de VENTAS del ERP (DynamiaERP). null = aún no cargado / sin datos.
  const [salesIncome, setSalesIncome] = useState(null);
  const [loadingSales, setLoadingSales] = useState(false);
  // Se activa si Firestore rechaza por permisos (reglas sin desplegar).
  const [rulesError, setRulesError] = useState(false);
  const rawPeriodLabel = month
    ? format(month, 'MMMM yyyy', { locale: es })
    : fromStr === toStr
      ? format(range.from, "EEEE d 'de' MMMM yyyy", { locale: es })
      : `${format(range.from, "d MMM", { locale: es })} – ${format(range.to, "d MMM yyyy", { locale: es })}`;
  const monthLabel = rawPeriodLabel.charAt(0).toUpperCase() + rawPeriodLabel.slice(1);

  const [categories, setCategories] = useState([]);
  const seededRef = useRef(false);
  const [catManagerOpen, setCatManagerOpen] = useState(false);

  const [expenses, setExpenses] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ amount: '', category: '', description: '', date: format(new Date(), 'yyyy-MM-dd'), kind: 'expense' });

  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(true);

  // Ventas del mes desde el ERP (una llamada por mes visible)
  useEffect(() => {
    let cancelled = false;
    setLoadingSales(true);
    fetchUnifiedReport(range.from, range.to)
      .then((res) => {
        if (cancelled) return;
        setSalesIncome(res?.success ? Number(res.data?.totalSales ?? 0) : null);
      })
      .catch(() => { if (!cancelled) setSalesIncome(null); })
      .finally(() => { if (!cancelled) setLoadingSales(false); });
    return () => { cancelled = true; };
  }, [fromStr, toStr, fetchUnifiedReport]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Categorías (Firestore) + siembra inicial ─────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'financeCategories'),
      async (snap) => {
        setRulesError(false);
        if (snap.empty && !seededRef.current) {
          seededRef.current = true;
          try {
            await Promise.all(
              DEFAULT_CATEGORIES.map((c) => setDoc(doc(db, 'financeCategories', c.id), { name: c.name, color: c.color, type: c.type }))
            );
          } catch (err) {
            console.error('No se pudieron sembrar categorías', err);
            if (err?.code === 'permission-denied') setRulesError(true);
          }
          return;
        }
        setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error('financeCategories listener error', err);
        if (err?.code === 'permission-denied') setRulesError(true);
      }
    );
    return unsub;
  }, []);

  const catFor = useCallback(
    (id) => categories.find((c) => c.id === id) ?? { name: id || 'Sin categoría', color: '#64748b' },
    [categories]
  );
  const categoriesOfKind = useCallback(
    (kind) => categories.filter((c) => c.type === kind).sort((a, b) => a.name.localeCompare(b.name)),
    [categories]
  );

  // ── Suscripción a ingresos + egresos del mes ─────────────────────────────────
  useEffect(() => {
    setLoadingList(true);
    const from = fromStr;
    const to = toStr;

    const sub = (coll, kind, setter) => {
      const q = query(collection(db, coll), where('date', '>=', from), where('date', '<=', to), orderBy('date', 'desc'));
      return onSnapshot(
        q,
        (snap) => { setRulesError(false); setter(snap.docs.map((d) => ({ id: d.id, ...d.data(), kind }))); setLoadingList(false); },
        (err) => { if (err?.code === 'permission-denied') setRulesError(true); setLoadingList(false); }
      );
    };

    const unsubE = sub('expenses', 'expense', setExpenses);
    const unsubI = sub('incomes', 'income', setIncomes);
    return () => { unsubE(); unsubI(); };
  }, [fromStr, toStr]);

  // ── Gráfica 6 meses (ingresos manuales + egresos) ────────────────────────────
  useEffect(() => {
    const fetchChart = async () => {
      setLoadingChart(true);
      const months = Array.from({ length: 6 }, (_, i) => subMonths(startOfMonth(new Date()), 5 - i));
      const rows = await Promise.all(
        months.map(async (m) => {
          const fromStr = format(startOfMonth(m), 'yyyy-MM-dd');
          const toStr = format(endOfMonth(m), 'yyyy-MM-dd');
          const sumAmount = async (coll) => {
            try {
              const snap = await getDocs(query(collection(db, coll), where('date', '>=', fromStr), where('date', '<=', toStr)));
              return snap.docs.reduce((s, d) => s + (d.data().amount ?? 0), 0);
            } catch { return 0; }
          };
          const [egresos, manualIncome] = await Promise.all([sumAmount('expenses'), sumAmount('incomes')]);
          let sales = 0;
          try {
            const r = await fetchUnifiedReport(startOfMonth(m), endOfMonth(m));
            sales = r?.success ? Number(r.data?.totalSales ?? 0) : 0;
          } catch { sales = 0; }
          const ingresos = manualIncome + sales;
          return { month: format(m, 'MMM', { locale: es }), ingresos, egresos, ganancia: ingresos - egresos };
        })
      );
      setChartData(rows);
      setLoadingChart(false);
    };
    fetchChart();
  }, [fetchUnifiedReport]);

  // ── Valores derivados ────────────────────────────────────────────────────────
  const totalEgresos = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const totalManualIncome = incomes.reduce((s, e) => s + (e.amount || 0), 0);
  const hasIncomeData = incomes.length > 0 || salesIncome !== null;
  const totalIngresos = totalManualIncome + (salesIncome ?? 0);
  const ganancia = hasIncomeData ? totalIngresos - totalEgresos : (totalEgresos > 0 ? -totalEgresos : null);
  const margen = totalIngresos > 0 ? ((ganancia ?? 0) / totalIngresos) * 100 : null;
  const esRentable = ganancia !== null && ganancia > 0;
  const enPerdida = ganancia !== null && ganancia < 0;

  const movements = [...incomes, ...expenses].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  const expensesByCategory = categoriesOfKind('expense')
    .map((c) => ({ name: c.name, value: expenses.filter((e) => e.category === c.id).reduce((s, e) => s + (e.amount || 0), 0), color: c.color }))
    .filter((d) => d.value > 0);

  // Sincronizar mes actual en la gráfica de tendencia
  useEffect(() => {
    if (!month) return; // solo cuando el período es un mes
    const label = format(month, 'MMM', { locale: es });
    setChartData((prev) => prev.map((d) => {
      if (d.month !== label) return d;
      const ing = totalIngresos;
      return { ...d, ingresos: ing, egresos: totalEgresos, ganancia: ing - totalEgresos };
    }));
  }, [totalIngresos, totalEgresos, month?.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Export PDF ───────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const pdf = new jsPDF();
    const periodStr = monthLabel;
    pdf.setFontSize(18); pdf.setTextColor(40, 40, 40); pdf.text(COMPANY_NAME, 14, 18);
    pdf.setFontSize(11); pdf.setTextColor(100, 100, 100);
    pdf.text(`Reporte Financiero — ${periodStr}`, 14, 26);
    pdf.text(`Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 32);
    pdf.setFontSize(12); pdf.setTextColor(40, 40, 40); pdf.text('Resumen', 14, 44);
    autoTable(pdf, {
      startY: 48,
      head: [['Concepto', 'Monto']],
      body: [
        ['Ingresos manuales', fmt(totalManualIncome)],
        ['Ingresos de ventas (ERP)', salesIncome !== null ? fmt(salesIncome) : 'Pendiente conectar'],
        ['Ingresos totales', hasIncomeData ? fmt(totalIngresos) : 'N/A'],
        ['Egresos (Gastos)', fmt(totalEgresos)],
        ['Ganancia Neta', ganancia !== null ? fmt(ganancia) : 'N/A'],
        ['Margen %', margen !== null ? `${margen.toFixed(1)}%` : 'N/A'],
        ['Rentabilidad', ganancia === null ? 'N/A' : ganancia > 0 ? 'Positiva (rentable)' : ganancia < 0 ? 'Negativa (pérdida)' : 'Equilibrio'],
      ],
      theme: 'striped', headStyles: { fillColor: [37, 99, 235] }, margin: { left: 14, right: 14 },
    });
    let y = pdf.lastAutoTable.finalY + 12;
    if (incomes.length > 0) {
      pdf.setFontSize(12); pdf.text('Ingresos manuales', 14, y);
      autoTable(pdf, {
        startY: y + 4,
        head: [['Fecha', 'Descripción', 'Categoría', 'Monto']],
        body: incomes.map((e) => [e.date, e.description, catFor(e.category).name, fmt(e.amount)]),
        theme: 'striped', headStyles: { fillColor: [16, 185, 129] }, margin: { left: 14, right: 14 },
      });
      y = pdf.lastAutoTable.finalY + 12;
    }
    pdf.setFontSize(12); pdf.text('Detalle de Egresos', 14, y);
    autoTable(pdf, {
      startY: y + 4,
      head: [['Fecha', 'Descripción', 'Categoría', 'Monto']],
      body: expenses.map((e) => [e.date, e.description, catFor(e.category).name, fmt(e.amount)]),
      theme: 'striped', headStyles: { fillColor: [244, 63, 94] }, margin: { left: 14, right: 14 },
    });
    pdf.save(`finanzas_${fromStr}_${toStr}.pdf`);
  };

  // ── Export CSV ───────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const rows = [
      ['Tipo', 'Fecha', 'Descripción', 'Categoría', 'Monto', 'Registrado por'],
      ...movements.map((e) => [
        e.kind === 'income' ? 'Ingreso' : 'Egreso',
        e.date,
        `"${(e.description || '').replace(/"/g, '""')}"`,
        catFor(e.category).name,
        (e.kind === 'income' ? e.amount : -e.amount).toFixed(2),
        e.createdBy || '',
      ]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `movimientos_${fromStr}_${toStr}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Dialog helpers ─────────────────────────────────────────────────────────────
  const openAdd = (kind) => {
    setEditing(null);
    const firstCat = categoriesOfKind(kind)[0]?.id ?? '';
    setForm({ amount: '', category: firstCat, description: '', date: format(new Date(), 'yyyy-MM-dd'), kind });
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({ amount: String(m.amount), category: m.category, description: m.description, date: m.date, kind: m.kind });
    setDialogOpen(true);
  };

  const setKind = (kind) => setForm((f) => ({ ...f, kind, category: categoriesOfKind(kind)[0]?.id ?? '' }));
  const closeDialog = () => { setDialogOpen(false); setEditing(null); };

  const handleSave = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast({ title: 'Monto inválido', variant: 'destructive' }); return; }
    if (!form.description.trim()) { toast({ title: 'Agrega una descripción', variant: 'destructive' }); return; }
    if (!form.category) { toast({ title: 'Selecciona una categoría', description: 'Crea una con el engranaje si no hay.', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateDoc(doc(db, collForKind(editing.kind), editing.id), {
          amount, category: form.category, description: form.description.trim(), date: form.date,
        });
        toast({ title: editing.kind === 'income' ? 'Ingreso actualizado' : 'Egreso actualizado' });
      } else {
        await addDoc(collection(db, collForKind(form.kind)), {
          amount, category: form.category, description: form.description.trim(), date: form.date,
          createdAt: serverTimestamp(), createdBy: user?.email ?? user?.uid ?? 'unknown',
        });
        toast({ title: form.kind === 'income' ? 'Ingreso registrado' : 'Egreso registrado' });
      }
      closeDialog();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
    setSaving(false);
  };

  const handleDelete = async (m) => {
    try {
      await deleteDoc(doc(db, collForKind(m.kind), m.id));
      toast({ title: m.kind === 'income' ? 'Ingreso eliminado' : 'Egreso eliminado' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const dialogCats = categoriesOfKind(form.kind);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} onClick={() => dismiss(t.id)}
            className={`cursor-pointer rounded-lg px-4 py-3 shadow-lg text-sm max-w-xs ${t.variant === 'destructive' ? 'bg-red-900/90 text-red-100' : 'bg-gray-800 text-white'}`}>
            <p className="font-semibold">{t.title}</p>
            {t.description && <p className="text-white/70 mt-0.5">{t.description}</p>}
          </div>
        ))}
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
        <DashboardHeader activeTab="finanzas" title="Finanzas" subtitle="Ingresos, egresos y rentabilidad" />

        {/* Controles: período + categorías */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 sm:p-4 mb-4 space-y-3">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="-mx-1 px-1 overflow-x-auto scrollbar-none">
              <DatePresetButtons value={showCustom ? 'custom' : preset} onChange={(p, r) => { setShowCustom(false); setPreset(p); setRange(r); }} />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setShowCustom((v) => !v)}
                className={`h-10 px-3 border ${showCustom ? 'bg-blue-600/20 border-blue-500/40 text-blue-200 hover:bg-blue-600/30' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}>
                <CalendarDays className="h-4 w-4" />Personalizado
              </Button>
              <Button onClick={() => setCatManagerOpen(true)} className="h-10 px-3 bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 ml-auto lg:ml-0">
                <Settings className="h-4 w-4" />Categorías
              </Button>
            </div>
            <p className="text-white/50 text-sm lg:ml-auto">{monthLabel}</p>
          </div>
          {showCustom && (
            <div className="grid grid-cols-2 sm:flex sm:items-end gap-3">
              <label className="text-xs text-white/50">Desde
                <input type="date" value={fromStr} max={toStr}
                  onChange={(e) => e.target.value && (setPreset('custom'), setRange((r) => ({ ...r, from: new Date(e.target.value + 'T00:00:00') })))}
                  className="mt-1 block w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-white text-sm" />
              </label>
              <label className="text-xs text-white/50">Hasta
                <input type="date" value={toStr} min={fromStr}
                  onChange={(e) => e.target.value && (setPreset('custom'), setRange((r) => ({ ...r, to: new Date(e.target.value + 'T23:59:59') })))}
                  className="mt-1 block w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-white text-sm" />
              </label>
            </div>
          )}
        </div>

        {/* Aviso: reglas de Firestore sin desplegar */}
        {rulesError && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-4 text-sm text-red-200">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              No se pueden leer/guardar los movimientos: faltan desplegar las reglas de Firestore de Finanzas
              (<code className="text-red-100">incomes</code>, <code className="text-red-100">expenses</code>, <code className="text-red-100">financeCategories</code>).
              Ejecuta <code className="text-red-100">firebase deploy --only firestore:rules</code> y recarga.
            </span>
          </div>
        )}

        {/* Tarjetas */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <SummaryCard title="Ingresos" value={hasIncomeData ? totalIngresos : null} icon={TrendingUp} color="#10b981"
            note={loadingSales
              ? 'cargando ventas…'
              : salesIncome !== null
                ? `ventas ${fmt(salesIncome)}${totalManualIncome > 0 ? ` · manual ${fmt(totalManualIncome)}` : ''}`
                : (totalManualIncome > 0 ? 'solo manual' : undefined)} />
          <SummaryCard title="Egresos" value={totalEgresos} icon={TrendingDown} color="#f43f5e" />
          <SummaryCard title="Ganancia Neta" value={ganancia} icon={DollarSign}
            color={esRentable ? '#10b981' : enPerdida ? '#f43f5e' : '#6366f1'}
            note={esRentable ? '✓ Negocio rentable' : enPerdida ? '⚠ Pérdida neta' : undefined}
            noteTone={esRentable ? 'good' : enPerdida ? 'bad' : 'muted'} />
          <SummaryCard title="Margen" value={margen} icon={Wallet}
            color={esRentable ? '#10b981' : enPerdida ? '#f43f5e' : '#f59e0b'} prefix="" suffix="%"
            note={margen !== null ? (margen >= 0 ? 'Rentabilidad sobre ingresos' : 'Margen negativo') : undefined}
            noteTone={margen !== null ? (margen >= 0 ? 'good' : 'bad') : 'muted'} />
        </div>

        {/* Estado de rentabilidad */}
        {ganancia !== null && (
          <div className={`flex items-center gap-3 rounded-xl border px-5 py-3 mb-6 ${esRentable ? 'border-emerald-500/25 bg-emerald-500/10' : enPerdida ? 'border-red-500/25 bg-red-500/10' : 'border-white/10 bg-white/5'}`}>
            <div className={`p-2 rounded-lg ${esRentable ? 'bg-emerald-500/20' : enPerdida ? 'bg-red-500/20' : 'bg-white/10'}`}>
              <Scale className={`h-4 w-4 ${esRentable ? 'text-emerald-400' : enPerdida ? 'text-red-400' : 'text-white/50'}`} />
            </div>
            <div className="min-w-0">
              <p className={`text-sm font-semibold ${esRentable ? 'text-emerald-300' : enPerdida ? 'text-red-300' : 'text-white/70'}`}>
                {esRentable ? 'El negocio está en positivo (rentable)' : enPerdida ? 'El negocio está en negativo (pérdida)' : 'Punto de equilibrio'}
              </p>
              <p className="text-xs text-white/45 mt-0.5">
                {monthLabel}: ingresos {hasIncomeData ? fmt(totalIngresos) : '—'} − egresos {fmt(totalEgresos)} ={' '}
                <span className={esRentable ? 'text-emerald-400' : enPerdida ? 'text-red-400' : 'text-white/60'}>
                  {ganancia < 0 ? '−' : ''}{fmt(Math.abs(ganancia))}
                </span>
                {margen !== null && <> · margen {margen.toFixed(1)}%</>}
              </p>
            </div>
          </div>
        )}

        {/* Gráficas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2 rounded-xl border border-white/8 bg-white/3 p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-white/70">Tendencia 6 meses — ingresos, gastos y ganancia</p>
              {loadingChart && <RefreshCw className="h-3.5 w-3.5 text-white/30 animate-spin" />}
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="gIngresos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gEgresos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="month" tick={{ fill: '#ffffff50', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={fmtShort} tick={{ fill: '#ffffff50', fontSize: 10 }} axisLine={false} tickLine={false} width={52} />
                <Tooltip content={<ChartTooltip />} />
                <Legend formatter={(v) => <span className="text-white/60 text-xs">{v}</span>} />
                <ReferenceLine y={0} stroke="#ffffff30" strokeDasharray="4 4" />
                <Area type="monotone" dataKey="ingresos" name="Ingresos" stroke="#10b981" strokeWidth={2} fill="url(#gIngresos)" />
                <Area type="monotone" dataKey="egresos" name="Egresos" stroke="#f43f5e" strokeWidth={2} fill="url(#gEgresos)" />
                <Line type="monotone" dataKey="ganancia" name="Ganancia" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3, fill: '#6366f1' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-xl border border-white/8 bg-white/3 p-5">
            <p className="text-sm font-semibold text-white/70 mb-4">Egresos por categoría</p>
            {expensesByCategory.length === 0 ? (
              <div className="flex items-center justify-center h-[220px] text-white/25 text-sm">Sin egresos este mes</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={expensesByCategory} dataKey="value" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={2}>
                      {expensesByCategory.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {expensesByCategory.map((cat) => (
                    <div key={cat.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: cat.color }} />
                        <span className="text-white/60">{cat.name}</span>
                      </div>
                      <span className="text-white/80 font-mono">{fmt(cat.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Lista de movimientos */}
        <div className="rounded-xl border border-white/8 bg-white/3 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-white/8">
            <div>
              <p className="font-semibold text-white text-sm">Movimientos — {monthLabel}</p>
              <p className="text-white/40 text-xs mt-0.5">{movements.length} registros · Ingresos manuales: {fmt(totalManualIncome)} · Egresos: {fmt(totalEgresos)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleExportCSV} disabled={movements.length === 0} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 h-9 px-3 disabled:opacity-30">CSV</Button>
              <Button onClick={handleExportPDF} disabled={movements.length === 0} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 h-9 px-3 disabled:opacity-30">PDF</Button>
              <Button onClick={() => openAdd('income')} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3">
                <ArrowUpCircle className="h-3.5 w-3.5 mr-1.5" />Ingreso
              </Button>
              <Button onClick={() => openAdd('expense')} className="bg-rose-600 hover:bg-rose-700 text-white h-9 px-3">
                <ArrowDownCircle className="h-3.5 w-3.5 mr-1.5" />Egreso
              </Button>
            </div>
          </div>

          {loadingList ? (
            <div className="p-8 text-center text-white/30 text-sm">Cargando…</div>
          ) : movements.length === 0 ? (
            <div className="p-8 text-center text-white/25 text-sm">No hay movimientos registrados en {monthLabel}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 border-b border-white/8">
                  <tr>
                    <th className="px-4 py-3 text-left text-white/40 font-medium">Tipo</th>
                    <th className="px-4 py-3 text-left text-white/40 font-medium">Fecha</th>
                    <th className="px-4 py-3 text-left text-white/40 font-medium">Descripción</th>
                    <th className="px-4 py-3 text-left text-white/40 font-medium hidden sm:table-cell">Categoría</th>
                    <th className="px-4 py-3 text-right text-white/40 font-medium">Monto</th>
                    <th className="px-4 py-3 w-20" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {movements.map((m) => {
                    const cat = catFor(m.category);
                    const isIncome = m.kind === 'income';
                    return (
                      <tr key={`${m.kind}-${m.id}`} className="hover:bg-white/3 transition-colors">
                        <td className="px-4 py-3">
                          {isIncome
                            ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><ArrowUpCircle className="h-3.5 w-3.5" />Ingreso</span>
                            : <span className="inline-flex items-center gap-1 text-xs text-rose-400"><ArrowDownCircle className="h-3.5 w-3.5" />Egreso</span>}
                        </td>
                        <td className="px-4 py-3 text-white/50 font-mono text-xs">{m.date}</td>
                        <td className="px-4 py-3 text-white/80">{m.description}</td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border"
                            style={{ color: cat.color, borderColor: cat.color + '40', background: cat.color + '15' }}>
                            {cat.name}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-right font-mono font-semibold ${isIncome ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {isIncome ? '+' : '−'}{fmt(m.amount)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEdit(m)} className="p-1.5 rounded text-white/25 hover:text-blue-400 hover:bg-blue-500/10 transition-colors" title="Editar">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => handleDelete(m)} className="p-1.5 rounded text-white/25 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Eliminar">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Diálogo agregar / editar */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent aria-describedby={undefined} className="bg-gray-900 border-white/10 text-white sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? (editing.kind === 'income' ? 'Editar ingreso' : 'Editar egreso') : (form.kind === 'income' ? 'Registrar ingreso' : 'Registrar egreso')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {!editing && (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setKind('income')}
                  className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm border transition-colors ${form.kind === 'income' ? 'bg-emerald-600/20 border-emerald-500/50 text-emerald-300' : 'bg-white/5 border-white/10 text-white/50 hover:text-white/80'}`}>
                  <ArrowUpCircle className="h-4 w-4" />Ingreso
                </button>
                <button onClick={() => setKind('expense')}
                  className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm border transition-colors ${form.kind === 'expense' ? 'bg-rose-600/20 border-rose-500/50 text-rose-300' : 'bg-white/5 border-white/10 text-white/50 hover:text-white/80'}`}>
                  <ArrowDownCircle className="h-4 w-4" />Egreso
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-white/60 text-xs">Monto (USD)</label>
                <Input type="number" min="0" step="0.01" value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0.00"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/25" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/60 text-xs">Fecha</label>
                <Input type="date" value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-white/60 text-xs">Categoría</label>
                <button onClick={() => setCatManagerOpen(true)} className="flex items-center gap-1 text-xs text-white/40 hover:text-white/80 transition-colors">
                  <Settings className="h-3.5 w-3.5" /> Editar
                </button>
              </div>
              {dialogCats.length === 0 ? (
                <p className="text-xs text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                  No hay categorías de {form.kind === 'income' ? 'ingreso' : 'egreso'}. Crea una con el engranaje ⚙.
                </p>
              ) : (
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={selectCls}>
                  {dialogCats.map((c) => (<option key={c.id} value={c.id} className="bg-gray-900">{c.name}</option>))}
                </select>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-white/60 text-xs">Descripción</label>
              <Input value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder={form.kind === 'income' ? 'ej. Venta en efectivo, abono cliente…' : 'ej. Alquiler, servicios, nómina…'}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25" />
            </div>
          </div>

          <DialogFooter>
            <Button onClick={closeDialog} className="bg-white/5 border border-white/10 text-white hover:bg-white/10 h-9 px-4">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4">
              {saving ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              {editing ? 'Guardar cambios' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CategoryManager open={catManagerOpen} onClose={() => setCatManagerOpen(false)} categories={categories} onToast={toast} />
    </div>
  );
}
